<script lang="ts">
	import { format } from 'svelte-i18n';

	//stores
	import { controlBleDevice, showBleControlSetupModal } from 'src/stores/features/ble/control-device';
	//helpers
	import { capFirstChar } from 'src/helpers/utilities';
	//services
	import { control_bluetooth_device_android } from 'src/services/native_container_requests_android';
	import { control_bluetooth_device_ios } from 'src/services/native_container_requests_ios';

	import TransitionWrapper from './Utility/TransitionWrapper.svelte';
	//DS
	import {
		Alert,
		Button,
		Container,
		Loader,
		Modal,
		ProgressBar,
		Text,
	} from '@Shelly/shelly-design-system';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { os } from 'src/stores/session/native-bridge';
	import { real_id } from 'src/helpers/interface_helpers';
	import { pairingMessageWrapper } from 'src/helpers/inclusion_helpers';
	import { devices_config } from '@devices/index';
	// import { onMount } from 'svelte';
	// import { mobile } from 'src/services/mobile-rework';

	const handleCloseModal = () => {
		/**
		 * Dismissing the firmware-update prompt on an already-connected device
		 * should keep the BLE session alive so the user can keep using the
		 * device settings. Only the pairing flow (or a not-yet-connected
		 * session) should tear the connection down on close.
		 */
		if ($showBleControlSetupModal === 'update' && $controlBleDevice.connectionState === 2) {
			// the OTA flow set processRunning when the update was requested; clear
			// it so the settings actions (incl. the Update button) re-enable
			controlBleDevice.updateProcessRunning(false);
			$showBleControlSetupModal = undefined;
			return;
		}
		if ($controlBleDevice.connectionState === 2) {
			$os === 'IOS'
				? control_bluetooth_device_ios($controlBleDevice.deviceId, 'disconnect')
				: control_bluetooth_device_android($controlBleDevice.deviceId, 'disconnect');
			controlBleDevice.reset();
		} else {
			$os === 'IOS'
				? control_bluetooth_device_ios($controlBleDevice.deviceId, 'abortConnecting')
				: control_bluetooth_device_android($controlBleDevice.deviceId, 'disconnect');
			controlBleDevice.reset();
		}
		controlBleDevice.updateProcessRunning(false);
		$showBleControlSetupModal = undefined;
	};

	const handleUpdateDenied = () => {
		/**
		 * Declining the update keeps the existing BLE session so the user can
		 * continue using the device settings. The native `updateDenied` command
		 * isn't supported in this control flow (it replies `unknownCommand`,
		 * which knocks the session into an error state), so we just dismiss the
		 * prompt without sending it.
		 */
		controlBleDevice.updateProcessRunning(false);
		$showBleControlSetupModal = undefined;
	};

	const handleUpdate = () => {
		const deviceId = real_id($controlBleDevice.deviceId);
		controlBleDevice.updateDeviceId(deviceId);
		controlBleDevice.updateProcessRunning(true);

		$os === 'IOS'
			? control_bluetooth_device_ios(deviceId, 'update')
			: control_bluetooth_device_android(deviceId, 'update');
	};

	// const handleControlDeviceStatus = e => {
	// 	const parsed_data = JSON.parse(e.detail);
	// 	// console.log('handleControlDeviceStatus', parsed_data);
	// };

	// onMount(() => {
	// 	mobile.addEventListener('controlling_device', handleControlDeviceStatus);

	// 	return () => {
	// 		mobile.removeEventListener(
	// 			'controlling_device',
	// 			handleControlDeviceStatus
	// 		);
	// 	};
	// });

	const semVer = /\d+(\.\d+){2,}/gi;
	$: realDevice = $devices[real_id($controlBleDevice.deviceId)];
	$: statusStore = getDeviceStatus($controlBleDevice.deviceId);
	$: versionInStatus = $statusStore?.['fwversion:0']?.version;

	$: currentFirmware =
		typeof realDevice?.fwID !== 'undefined' &&
		typeof realDevice?.fwID?.match(semVer)?.toString() !== 'undefined'
			? realDevice?.fwID?.match(semVer)?.toString()
			: typeof versionInStatus !== 'undefined' && versionInStatus !== ''
				? versionInStatus
				: '1.0.0';
</script>

<Modal
	on:close={handleCloseModal}
	showModal={$showBleControlSetupModal}
	title={$showBleControlSetupModal == 'pair'
		? $format('modal_title_ble_pair', {
				default: 'Bluetooth pair',
			})
		: $format('modal_title_ble_update', {
				default: 'Firmware update',
			})}
	backdropClose={false}
	hideSlotFooter={$showBleControlSetupModal == 'update' &&
		(!!$controlBleDevice.updateProgress || $controlBleDevice.processRunning)}
	removeScrollPadding
>
	<div slot="content" class="content-container">
		{#if $showBleControlSetupModal == 'pair'}
			<TransitionWrapper>
				<Container padding="scrollPaddings">
					<Alert
						heading={capFirstChar(
							$format('alert_title_ble_pair_prompt', {
								default: 'Pairing required',
							}),
						)}
						message={pairingMessageWrapper(realDevice?.type, currentFirmware)}
					/>
				</Container>
			</TransitionWrapper>
		{:else if $showBleControlSetupModal == 'update'}
			{#if $controlBleDevice.updateProgress}
				<Container padding="scrollPaddings">
					<TransitionWrapper>
						<Text class="mb-m"
							>{$format('fw_update_in_progress_description', {
								default: `FW update in progress...`,
							})}</Text
						>

						{#if $controlBleDevice.updateProgress === 100 || $controlBleDevice.connectionState === 0}
							<Alert
								heading={capFirstChar(
									$format('press_button_required', {
										default: 'Please press device button.',
									}),
								)}
							/>
						{:else}
							<ProgressBar
								class="mb-m width-full"
								type="primary"
								progress={$controlBleDevice.updateProgress}
							/>
							<Alert
								heading={capFirstChar(
									$format('alert_title_please_wait_for_update', {
										default: 'Please wait while we update your device.',
									}),
								)}
								message={capFirstChar(
									$format('alert_description_please_wait_for_update', {
										default:
											'Please do not interrupt this process or close the app while the device is updating.',
									}),
								)}
							/>
						{/if}
					</TransitionWrapper>
				</Container>
			{:else if $controlBleDevice.processRunning}
				<Container padding="scrollPaddings">
					<TransitionWrapper>
						<div class="d-flex align-items-center flex-direction-column">
							<Loader size="m" />

							<Text styleType="controls-small" class="mt-xs text-bold">
								{capFirstChar(
									$format('progress_state_initializing_update', {
										default: 'initializing update',
									}),
								)}...
							</Text>
						</div>

						<Alert
							type="info"
							class="mt-m"
							isHeadingBold
							heading={capFirstChar(
								$format('press_button_required', {
									default: 'Please press device button.',
								}),
							)}
							message={$format('alert_message_press_button_to_continue_update', {
								default:
									'Press the device button to wake it up and continue the firmware update.',
							})}
						/>
					</TransitionWrapper>
				</Container>
			{:else}
				<TransitionWrapper>
					<Container padding="scrollPaddings">
						<Alert
							heading={capFirstChar(
								$format('alert_title_ble_update_prompt', {
									default: 'Update available',
								}),
							)}
							message={capFirstChar(
								$format('alert_message_ble_update_description', {
									values: {
										device_type:
											devices_config[realDevice?.type]?.friendly_name ??
											realDevice?.type ??
											'',
									},
									default: `A new firmware version is available for your ${
										devices_config[realDevice?.type]?.friendly_name ?? 'device'
									}`,
								}),
							)}
						/>
					</Container>
				</TransitionWrapper>
			{/if}
		{/if}
	</div>

	<div slot="footer">
		{#if $showBleControlSetupModal == 'pair'}
			<div class="d-flex flex-direction-column width-full">
				<Button
					variant="secondary"
					class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap align-self-end"
					on:click={handleCloseModal}
					responsiveSize={{ sm: 'm' }}
				>
					{$format('button_cancel')}
				</Button>
			</div>
		{:else if $showBleControlSetupModal == 'update' && !$controlBleDevice.updateProgress && !$controlBleDevice.processRunning}
			<div class="d-flex justify-content-end width-full">
				<Button
					class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
					responsiveSize={{
						sm: 'm',
					}}
					variant="secondary"
					on:click={handleUpdateDenied}>{$format('button_cancel')}</Button
				>
				<Button
					responsiveSize={{
						sm: 'm',
					}}
					class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
					on:click={handleUpdate}
				>
					{capFirstChar($format('button_update'))}
				</Button>
			</div>
		{/if}
	</div>
</Modal>
